import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FiUsers, FiX } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import { createGroup } from "../api/messages";
import useLockBackgroundScroll from "../hooks/useLockBackgroundScroll";
import GroupMembersPicker from "./GroupMembersPicker";

const MAX_NAME = 60;

/**
 * Start a group chat: a name, and the people in it. `onCreated(conversation)` once it
 * exists — the people added are in it straight away.
 */
const NewGroupModal = ({ open, onClose, onCreated, isDark, setToast }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [people, setPeople] = useState([]);
  const [saving, setSaving] = useState(false);

  useLockBackgroundScroll(open);

  useEffect(() => {
    if (!open) {
      setName("");
      setPeople([]);
      return undefined;
    }
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const ready = name.trim() && people.length > 0 && !saving;
  const create = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setSaving(true);
    try {
      const res = await createGroup(name.trim(), people.map((p) => String(p._id)));
      if (res.success) onCreated(res.data);
    } catch (error) {
      setToast?.(error?.response?.data?.message || t("groups.createError"));
    } finally {
      setSaving(false);
    }
  };

  const muted = "text-label-secondary";
  const base = "text-label-primary";

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center sm:items-center sm:p-4 bg-overlay-scrim"
      onClick={onClose}
      data-scroll-lock-modal
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-label={t("groups.newGroup")}
        onSubmit={create}
        onClick={(e) => e.stopPropagation()}
        className={`flex h-[85vh] max-h-[640px] w-full flex-col overflow-hidden rounded-t-radius-xl border shadow-overlay sm:max-w-md sm:rounded-radius-lg ${
          "border-separator-default bg-bg-raised"
        }`}
      >
        <div className={`flex items-center gap-3 border-b px-4 py-3 border-separator-default`}>
          <span className="grid h-9 w-9 place-items-center rounded-full bg-accent-tinted text-accent-text">
            <FiUsers className="h-4 w-4" />
          </span>
          <h2 className={`flex-1 text-headline ${base}`}>{t("groups.newGroup")}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className={`rounded-radius-xs p-2 transition hover:bg-fill-quaternary`}
          >
            <FiX className={`h-5 w-5 ${muted}`} />
          </button>
        </div>

        <div className="px-4 pb-3 pt-4">
          <label className={`mb-1.5 block text-footnote-semibold ${muted}`} htmlFor="new-group-name">
            {t("groups.name")}
          </label>
          <input
            id="new-group-name"
            value={name}
            maxLength={MAX_NAME}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("groups.namePlaceholder")}
            autoFocus
            className={`w-full rounded-radius-sm border px-3 py-2.5 text-subheadline outline-none transition focus:border-accent-default ${
              "border-separator-default bg-bg-raised text-label-primary placeholder-label-tertiary"
            }`}
          />
        </div>

        <p className={`px-4 pb-2 text-footnote-semibold ${muted}`}>{t("groups.addPeople")}</p>
        <GroupMembersPicker selected={people} onChange={setPeople} isDark={isDark} />

        <div className={`flex items-center justify-between gap-3 border-t px-4 py-3 border-separator-default`}>
          <span className={`text-footnote ${muted}`}>
            {people.length ? t("groups.peopleSelected", { count: people.length }) : t("groups.pickSomeone")}
          </span>
          <button
            type="submit"
            disabled={!ready}
            className="rounded-radius-sm bg-accent-default px-5 py-2.5 text-subheadline-semibold text-accent-on transition hover:bg-mintStrong disabled:cursor-not-allowed disabled:opacity-40"
          >
            {saving ? t("groups.creating") : t("groups.create")}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default NewGroupModal;
